import { AxiosResponse } from 'axios';
import { MyIcon } from 'ccs-ui-components';
import { Dispatch, SetStateAction } from 'react';

import { FULFILLED, SERVICE_SORT_LABELS, STATUS_TYPES, URL_PATH, validErrorCodes } from '../../constants';
import { IEgressConfig, IEgressEventMappingModel, IEgressEventModel, IExternalTopic, IIngressConfig, IIngressEventMappingModel, IIngressEventModel, IIngressExceptionModel, INameSpaceParam, INavBarState, IService, IServiceOperation, IServiceOperationRequestModel, ISideNav, NavBarState } from '../../models';
import { TranslateHook } from '../../modules/schema-generator/Utils';
import { convertArrayToObject } from './ServiceDetailsUtils';
import { getAssociatedIngressEgressEvents, getAssociatedIngressEvents } from './ServiceEventAssociationUtils';

import classes from '../../_components/create-service-sidenav/CreateServiceSideNav.module.scss';
import { IServiceFilterState } from '../../store/reducer/services/ServiceFilterReducer';

const isLastModifiedEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SERVICE_SORT_LABELS.LASTMODIFIED;

const isStatusEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SERVICE_SORT_LABELS.STATUS;

export const isServiceNameEqual = (queryParam: { [key: string]: string }) => queryParam.sort === SERVICE_SORT_LABELS.SERVICENAME;

export const getStatusParam = (queryParam: { [key: string]: string }, status?: string) => (status && status !== '') || queryParam.status !== undefined;

/**
 * @method getSortStateCondition
 * @description To get sortState - sortStateCondition
 */
export const getSortStateCondition = (queryParam: { [key: string]: string }) => {
  if (isLastModifiedEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  if (isServiceNameEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  return 'updatedTimestamp,DESC';
};

/**
 * @method getSortState
 * @description To get status - sortState
 */
export const getSortState = (queryParam: { [key: string]: string }) => {
  if (isStatusEqual(queryParam)) {
    return `${queryParam.sort},${queryParam.type}`;
  }
  return getSortStateCondition(queryParam);
};

/**
 * @method getIngressEventCount
 * @description Gets the ingress event count
 * @param serviceOperationName Service Operation Name
 * @param serviceOprtationEvents service event association data
 * @returns ingress event count
 */
export const getIngressEventCount = (serviceOperationName: string, ingressEventMappingList: IIngressEventMappingModel[]) => {
  const serviceOperationArray = ingressEventMappingList.filter((ingressEvent) => ingressEvent.serviceOperationName === serviceOperationName);
  return serviceOperationArray?.length ?? 0;
};

/**
 * @method getEgressEventCount
 * @description Gets the egress event count
 * @param serviceOperationName Service Operation Name
 * @param serviceOprtationEvents service event association data
 * @returns egress event count
 */
export const getEgressEventCount = (serviceOperationName: string, egressEventMappingList: IEgressEventMappingModel[]) => {
  const serviceOperationArray = egressEventMappingList.filter((egressEvent) => egressEvent.serviceOperationName === serviceOperationName);
  return serviceOperationArray?.length ?? 0;
};

/**
 * @method getSortLabelCondition
 * @description To get labelStatusCondition
 */
export const getSortLabelCondition = (queryParam: { [key: string]: string }) => {
  if (isLastModifiedEqual(queryParam)) {
    return queryParam.sort;
  }
  return SERVICE_SORT_LABELS.LASTMODIFIED;
};

/**
 * @method getSortLabel
 * @description To get labelStatus - sortLabel
 */
export const getSortLabel = (queryParam: { [key: string]: string }) => {
  if (isStatusEqual(queryParam)) {
    return queryParam.sort;
  }
  return getSortLabelCondition(queryParam);
};

/**
 * @method getQueryParamCondition
 * @description To get Query param condition
 */
export const getQueryParamCondition = (queryParam: { [key: string]: string }) => (queryParam.status?.split(',').length >= 5 ? '' : queryParam.status);

/**
 * @method isViewService
 * @param url : url string
 * @returns boolean if view service flow else false;
 */
const isViewService = (url: string) => url.includes('viewServiceDetails') || url.includes('viewExceptions') || url.includes('viewServiceOperation');

/**
 * @method getServiceNameFromUrl
 * @description gets the service name from the url
 * @returns service name
 */
export const getServiceNameFromUrl = (): string => {
  const { pathname } = window.location;
  const urlArray = pathname.split('/');
  try {
    if (isViewService(pathname) && urlArray && urlArray.length >= 4) {
      return urlArray[urlArray.length - 3];
    }
  } catch (error) {
    return '';
  }
  return '';
};

/**
 * @method getVersionFromUrl
 * @description gets the service version from the url
 * @returns service name
 */
export const getVersionFromUrl = (): string => {
  const { pathname } = window.location;
  const urlArray = pathname.split('/');
  try {
    if (isViewService(pathname) && urlArray && urlArray.length >= 4) {
      return urlArray[urlArray.length - 2];
    }
  } catch (error) {
    return '';
  }
  return '';
};

/**
 * @method getModeFromUrl
 * @description gets the service mode from the url
 * @returns service mode (edit/view)
 */
export const getModeFromUrl = (): string => {
  const { pathname } = window.location;
  const urlArray = pathname.split('/');
  try {
    if (isViewService(pathname) && urlArray && urlArray.length >= 4) {
      return urlArray[urlArray.length - 1];
    }
  } catch (error) {
    return '';
  }
  return '';
};

/**
 * @method getServicePath
 * @description gets the url path for create/view service
 * @param serviceName: service name
 * @returns url path object
 */
export const getServicePath = (serviceName: string, mode: string, version: string) => {
  const buildViewPath = (base: string) =>
    [base, serviceName, version, mode].filter(Boolean).join('/');
  return {
    createServiceDetails: URL_PATH.CREATE_SERVICE,
    exception: URL_PATH.CREATE_EXCEPTION,
    serviceOperation: URL_PATH.CREATE_SERVICE_OPERATION,
    viewServiceDetails: buildViewPath(URL_PATH.VIEW_SERVICE),
    viewException: buildViewPath(URL_PATH.VIEW_EXCEPTION),
    viewServiceOperation: buildViewPath(URL_PATH.VIEW_SERVICE_OPERATION)
  };
};

/**
 * @method arrayBufferToString
 * @description Converts an array buffer to a string representation.
 * @param exportedPrivateKey - The array buffer to convert.
 * @returns The string representation of the array buffer.
 */
export const arrayBufferToString = (exportedPrivateKey: any) => {
  const byteArray = new Uint8Array(exportedPrivateKey);
  let byteString = '';
  byteArray.forEach((data) => {
    byteString += String.fromCodePoint(data);
  });
  return byteString;
};

/**
 * @method getSideNavData
 * @description Gets the side nav bar object
 * @param serviceName: service name
 * @returns side nav bar data object
 */
export const getSideNavData = (serviceName: string, mode: string, isView: boolean, version: string, translate: TranslateHook) => {
  const servicePath = getServicePath(serviceName, mode, version);
  const sidebarData: ISideNav = {
    serviceDetails: {
      title: translate('serviceWrapper.txt_service_details'),
      path: isView ? servicePath.viewServiceDetails : servicePath.createServiceDetails,
      icon: <MyIcon name="checkCircleIcon" />,
      isEnabled: true
    },
    serviceExceptions: {
      title: translate('serviceWrapper.txt_exception'),
      path: isView ? servicePath.viewException : servicePath.exception,
      icon: <MyIcon name="checkCircleIcon" />,
      isEnabled: false
    },
    serviceOperations: {
      title: translate('serviceWrapper.txt_service_operation'),
      path: isView ? servicePath.viewServiceOperation : servicePath.serviceOperation,
      icon: <MyIcon name="checkCircleIcon" />,
      isEnabled: false
    }
  };
  return sidebarData;
};

/**
 * @method keyValueMapper
 * @description Maps the key value pair
 * @param object data object
 * @returns mapped object
 */
export const keyValueMapper = (object: { key: string, value: string }[]) => {
  const mappedObject: { [key: string]: string } = {};
  if (Array.isArray(object)) {
    object.forEach((item) => {
      mappedObject[`${item.key}`] = item.value;
    });
  }
  return mappedObject;
};

/**
 * @description Takes array of objects having prefix,namespace, isEdit and index.
 * @param arr
 * @returns object with prefix and namespace
 */
export const prefixNamespaceMapper = (arr: INameSpaceParam[]) => arr.map(({ prefix, namespace }) => ({ prefix, namespace }));

/**
 * @method paginationParamUtil
 * @description To set the items per page and page number for pagination
 */
export const paginationParamUtil = (setPage: Dispatch<SetStateAction<number>>, setItem: Dispatch<SetStateAction<number>>, queryParams: { [key: string]: string }, defaultPageSize: number) => {
  setPage(queryParams.page === undefined || queryParams.page === '' ? 0 : Number(queryParams.page));
  setItem(queryParams.item === undefined || queryParams.item === '' ? defaultPageSize : Number(queryParams.item));
};

/**
 * @method updateNavBarStatus
 * @description Update the Nav bar status
 * @param navBarState Current nav bar state
 * @param sideBarData Nav bar data object
 * @returns update nav bar data object
 */
export const updateNavBarStatus = (navBarState: INavBarState, sideBarData: ISideNav) => {
  const newSideBarData = { ...sideBarData };
  Object.keys(sideBarData).forEach((key) => {
    if (key === 'serviceDetails') {
      if (navBarState.serviceDetails.state === NavBarState.COMPLETE) {
        newSideBarData.serviceExceptions.isEnabled = true;
        newSideBarData.serviceOperations.isEnabled = true;
      } else if (navBarState.serviceDetails.state === NavBarState.INCOMPLETE
        || navBarState.serviceDetails.state === NavBarState.INCORRECT) {
        newSideBarData.serviceExceptions.isEnabled = false;
        newSideBarData.serviceOperations.isEnabled = false;
      }
    }
    const { state } = navBarState[key as keyof INavBarState];

    const statusIcon = {
      0: <MyIcon name="checkCircleFillIcon" className={classes['sidenav-check-fill-icon']} />, // Complete
      1: <MyIcon name="checkCircleIcon" className={classes['sidenav-check-icon']} />, // Incomplete
      2: <MyIcon name="xCircleIcon" className={classes['sidenav-incorrect-icon']} />// Incorrect
    };
    newSideBarData[key as keyof ISideNav].icon = statusIcon[state];
  });

  return newSideBarData;
};

/**
 * @method getIngressEventException
 * @description gets the exception associated with the event
 * @param exceptionList exception list
 */
const getIngressEventException = (exceptionList: IIngressExceptionModel[]) => exceptionList?.map((exception: IIngressExceptionModel) => ({
  ersExceptionName: exception.exceptionId,
  errorCode: exception.errorCode,
  actions: exception.action
}));

/**
 * @method getIngressConfig
 * @description Retrieves the ingress configuration based on the provided ingress event mapping model.
 * @param ingressEvent the ingress event mapping model.
 * @returns ingress configuration
 */
const getIngressConfig = (ingressEvent: IIngressEventMappingModel) => {
  const ingressEventConfig: IIngressConfig = {
    requestSpecJson: ingressEvent?.reqAttribute || null,
    payloadToQueryPathSpecJson: ingressEvent?.payloadToQueryPath || null,
    payloadToRequestHeaderSpecJson: ingressEvent?.payloadToRequestHeader || null,
    headerToRequestHeaderSpecJson: ingressEvent?.headerToRequestHeader || null,
    headerToRequestPayloadSpecJson: ingressEvent?.headerToRequestPayload || null,
    authSpecJson: ingressEvent?.requestToAuthenticationPayload || null,
    mqRequestSpec: ingressEvent?.mqRequestMapping,
    exceptionMappings: getIngressEventException(ingressEvent?.exceptions)
  };
  return ingressEventConfig;
};

const getConnectionString = (connection: string | undefined) => connection || '';

const getPartitionId = (partition: string | undefined) => (partition ? +partition : null);

const isExternalTopicValid = (egressEvent: IEgressEventMappingModel) => {
  if (egressEvent.topic) { return true; }
  return false;
};

/**
 * @method getEgressConfig
 * @description Retrieves the egress configuration based on the provided egress event mapping model.
 * @param egressEvent The egress event mapping model
 * @returns The egress configuration
 */
const getEgressConfig = (egressEvent: IEgressEventMappingModel) => {
  const externalTopic: IExternalTopic[] = [];
  const { topic, connection, partition } = egressEvent;
  externalTopic.push(
    {
      topicName: topic || '',
      connectionString: getConnectionString(connection),
      partitionId: getPartitionId(partition),
    }
  );
  const egressEventToMap: IEgressConfig = {
    responseSpecJson: egressEvent.resAttribute || null,
    mqResponseSpec: egressEvent?.mqResAttribute,
    externalTopic: isExternalTopicValid(egressEvent) ? externalTopic : []
  };
  return egressEventToMap;
};

/**
 * @method getEgressEventConfig
 * @description Retrieves the egress event configuration based on the provided egress event list.
 * @param egressEventList The list of egress events.
 * @returns The egress configuration list.
 */
const getEgressEventConfig = (egressEventList: IEgressEventMappingModel[]) => {
  const egressConfigList = egressEventList.map((egressEvent) => {
    const egressEventToMap: IEgressEventModel = {
      egressEventName: egressEvent.eventName,
      egressEventVersion: egressEvent.eventVersion || '',
      egressConfig: getEgressConfig(egressEvent)
    };
    return egressEventToMap;
  });
  return egressConfigList;
};

/**
 * @method getServiceConfigObject
 * @description Retrieves the service configuration object based on the provided parameters.
 * @param serviceOperation - The service operation.
 * @param ingressEventList - The list of ingress event mappings.
 * @param egressEventList - The list of egress event mappings.
 * @returns The service configuration object.
 */
const getServiceConfigObject = (
  serviceOperation: IServiceOperation,
  ingressEventList: IIngressEventMappingModel[],
  egressEventList: IEgressEventMappingModel[]
) => {
  const ingressEventToMap = getAssociatedIngressEvents(ingressEventList, serviceOperation.serviceOperation);
  const ingressEvent: IIngressEventModel[] = [];
  ingressEventToMap.forEach((ingress) => {
    const egressEventToMap = getAssociatedIngressEgressEvents(egressEventList, serviceOperation.serviceOperation, ingress.eventName, ingress.eventVersion);
    ingressEvent.push({
      ingressEventName: ingress?.eventName,
      ingressEventVersion: ingress?.eventVersion || '1',
      ingressConfig: getIngressConfig(ingress),
      egressEvents: getEgressEventConfig(egressEventToMap)
    });
  });

  return ingressEvent;
};

/**
 * @method getServiceOperationRequestObject
 * @description Construct the service operation request object
 * @param operation Service operation
 * @returns Service Operation request object
 */
export const getServiceOperationRequestObject = (
  operation: IServiceOperation,
  ingressEventList: IIngressEventMappingModel[],
  egressEventList: IEgressEventMappingModel[]
) => {
  const mappedOperation: IServiceOperationRequestModel = {
    serviceOperation: operation.serviceOperation,
    operationUrl: operation.operationUrl ? operation.operationUrl : null,
    operationVerb: operation.operationVerb ? operation.operationVerb : null,
    requestAttributes: operation?.requestAttributes,
    responseAttributes: operation?.responseAttributes,
    security: operation.security,
    retry: operation.retry || { errorCode: [], count: '' },
    timeout: operation.timeOut && operation.timeOut.trim() ? (Number.isNaN(parseInt(operation.timeOut, 10)) ? undefined : parseInt(operation.timeOut, 10)) : undefined,
    ingressEvents: getServiceConfigObject(operation, ingressEventList, egressEventList)
  };
  return mappedOperation;
};

/**
 * @method isErrorCodeValid
 * @description Checks if the provided error code is valid.
 * @param errorCode - The error code to check.
 * @returns A boolean indicating whether the error code is valid or not.
 */
export const isErrorCodeValid = (errorCode: number) => validErrorCodes.some((code) => code === errorCode.toString());

/**
 * @method serviceOperationMapper
 * @description Maps service operations to service operation request models.
 * @param serviceOperations - The array of service operations.
 * @param ingressEventList - The array of ingress event mappings.
 * @param egressEventList - The array of egress event mappings.
 * @returns An array of mapped service operation request models.
 */
export const serviceOperationMapper = (
  serviceOperations: IServiceOperation[],
  ingressEventList: IIngressEventMappingModel[],
  egressEventList: IEgressEventMappingModel[]
) => serviceOperations.map((operation) => {
  const mappedOperation: IServiceOperationRequestModel = getServiceOperationRequestObject(operation, ingressEventList, egressEventList);
  if (operation.queryPathParam) {
    mappedOperation.queryPathParam = operation.queryPathParam ? convertArrayToObject(operation.queryPathParam) : {};
  }
  if (operation.headerParam) {
    mappedOperation.headerParam = operation.headerParam ? convertArrayToObject(operation.headerParam) : {};
  }
  return mappedOperation;
});

/**
 * @method isDeleteServiceOperationDisabled
 * @description Checks if Delete service operation button should disabled
 * @param serviceOperationName Service operation name
 * @param isEditService isEditService
 * @param activeServiceOperationEvent Active service operation event
 * @returns true if disabled else false
 */
export const isDeleteServiceOperationDisabled = (
  serviceOperationName: string,
  isEditService: boolean,
  ingressEventList: IIngressEventMappingModel[],
  egressEventList: IEgressEventMappingModel[]
) => {
  if (isEditService) {
    const ingressEventCount = getIngressEventCount(serviceOperationName, ingressEventList);
    const egressEventCount = getEgressEventCount(serviceOperationName, egressEventList);
    if (egressEventCount || ingressEventCount) {
      return true;
    }
    return false;
  }
  return false;
};

/**
 * @method getClassNameForStatus
 * @description gets the styles based on status
 * @param status : service/ event status
 * @param primaryClassName : parent class name
 * @param classes : css classes
 * @returns style based on status
 */
export const getClassNameForStatus = (
  status: string,
  primaryClassName: string,
  classes: { readonly [key: string]: string }
) => {
  const serviceStatus = status?.toLocaleUpperCase();
  let className = primaryClassName;
  switch (serviceStatus) {
    case STATUS_TYPES.ACTIVE:
      className += ` ${classes.active}`;
      break;
    case STATUS_TYPES.DRAFT:
      className += ` ${classes.draft}`;
      break;
    case STATUS_TYPES.INACTIVE:
      className += ` ${classes.inactive}`;
      break;
    case STATUS_TYPES.PENDING_APPROVAL:
      className += ` ${classes.readyforreview}`;
      break;
    case STATUS_TYPES.RETURNED:
      className += ` ${classes.returned}`;
      break;
    default:
      break;
  }
  return className;
};

export const exportConfigConst = {
  contentDisposition_text: 'Content-Disposition',
  download_text: 'download'
};

/**
 * @method statusProvider
 * @description Provides the status based on the URL status.
 * @param UrlStatus - The URL status.
 * @returns The status based on the URL status.
 */
export const statusProvider = (UrlStatus: string) => {
  if (UrlStatus?.toLowerCase() === STATUS_TYPES.PENDING_APPROVAL.toLowerCase()) {
    return STATUS_TYPES.PENDING_APPROVAL;
  }
  return UrlStatus;
};

/**
 * @method getHeaderAttribute
 * @description Gets the decrypted xml
 * @param headerAttribute : xml
 * @returns xml
 */
export const getHeaderAttribute = (headerAttribute: PromiseSettledResult<AxiosResponse<any, any> | null>, activeService: IService) => (headerAttribute?.status === FULFILLED ? headerAttribute.value?.data.headerAttribute : activeService?.soapAttributes?.headersXML);

/**
 * Contains constant value for the default event list status.
 */
export const defaultServiceListStatus = `${STATUS_TYPES.ACTIVE},${STATUS_TYPES.DRAFT},${STATUS_TYPES.PENDING_APPROVAL},${STATUS_TYPES.RETURNED}`;
export const defaultServiceListStatusArray: string[] = [
  STATUS_TYPES.ACTIVE,
  STATUS_TYPES.DRAFT,
  STATUS_TYPES.PENDING_APPROVAL,
  STATUS_TYPES.RETURNED
];

export const parseSortString = (val?: string) => {
  if (!val) return { sort: '', type: '' };
  const [sort, type] = val.split(',');
  return { sort: sort ?? '', type: type ?? '' };
};

/**
 *
 * @method getFilterParams
 * @description Gets the current filter parameters.
 * If the search url is present, it will return the search url parameters.
 * Otherwise, it will return the current filters from the redux store.
 * @param search - The search url
 * @param filterState - The current filter state
 * @returns The filter parameters
 */
export const getFilterParams = (search: string, filterState: IServiceFilterState) => {
  const params = new URLSearchParams(search);
  const parsedParams = {
    status: params.get('status') ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    serviceName: params.get('serviceName') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
    sort: params.get('sort') ?? '',
    type: params.get('type') ?? ''
  };

  const hasUrlFilter = parsedParams.status || parsedParams.serviceName || parsedParams.fromDate || parsedParams.toDate || parsedParams.sort || parsedParams.type;
  if (hasUrlFilter) {
    return parsedParams;
  }

  const { sort, type } = parseSortString(filterState.sort);
  return {
    status: filterState.status ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
    serviceName: filterState.searchText ?? '',
    fromDate: String(filterState.fromDate ?? ''),
    toDate: String(filterState.toDate ?? ''),
    sort,
    type
  };
};
